// The call window (COMX-FE-08): a call opens in its own small browser window, like the call
// windows of Teams or WhatsApp, so the person keeps working in the app beside it. The window shows
// the route `/calls/$meetingId` (no shell, no chat). When the browser blocks the window, the call
// opens in the current tab instead. Nothing secret travels here: only the meeting id in the path.

/** Every call window is named with this prefix (one window per call). */
export const CALL_WINDOW_PREFIX = 'batiflow-call-';

const WIDTH = 880;
const HEIGHT = 640;

/** The address of the call screen. */
export const callPath = (meetingId: string): string => `/calls/${encodeURIComponent(meetingId)}`;

/** True in a window opened for a call (it shows no incoming-call toasts or notices of its own). */
export function isCallWindow(): boolean {
  return typeof window !== 'undefined' && window.name.startsWith(CALL_WINDOW_PREFIX);
}

/** Size and place of the window: centred on the app's window. */
function features(): string {
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - WIDTH) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - HEIGHT) / 2));
  return `popup=yes,width=${String(WIDTH)},height=${String(HEIGHT)},left=${String(left)},top=${String(top)}`;
}

function open(url: string, name: string): Window | null {
  try {
    // `window.open` is missing or throws in some embedded browsers: treated as blocked.
    return typeof window.open === 'function' ? window.open(url, name, features()) : null;
  } catch {
    return null;
  }
}

/**
 * Opens the window of a known call (Accept, Join). Must run in the click handler, or the browser
 * blocks it. Returns false when it was blocked: the caller opens the call in the tab then.
 */
export function openCallWindow(meetingId: string): boolean {
  const opened = open(callPath(meetingId), `${CALL_WINDOW_PREFIX}${meetingId}`);
  if (!opened) return false;
  opened.focus();
  return true;
}

/**
 * Opens an empty call window at once, in the click handler, for a call that the API has not
 * created yet ("Appeler"). Null when the browser blocked it.
 */
export function reserveCallWindow(): Window | null {
  return open('about:blank', `${CALL_WINDOW_PREFIX}${String(Date.now())}`);
}

/** Shows the call in a window opened by `reserveCallWindow`. */
export function showCallIn(target: Window, meetingId: string): void {
  target.location.href = callPath(meetingId);
  target.focus();
}

/** Closes the call window after the person hung up. Does nothing in a normal tab. */
export function closeCallWindow(): boolean {
  if (!isCallWindow()) return false;
  window.close();
  return true;
}
